import { useMemo } from 'react';
import { useIntl } from 'react-intl';

const useEnhancedSafetyCheck = (instance, holdings, items) => {
  const intl = useIntl();

  return useMemo(() => {
    // Basic checks
    const hasHoldings = holdings && holdings.length > 0;
    const flatItems = Object.values(items || {}).flat();
    const hasItems = flatItems && flatItems.length > 0;

    // Enhanced analysis for items
    const itemAnalysis = {
      totalItems: flatItems.length,
      availableItems: 0,
      unavailableItems: 0,
      unavailableItemsDetails: [],
    };

    if (hasItems) {
      flatItems.forEach((item) => {
        const status = item.status?.name || item.status || intl.formatMessage({
          id: 'ui-plugin-inventory-templates.safeDelete.status.unknown',
          defaultMessage: 'Unknown',
        });
        const isAvailable = status.toLowerCase() === 'available';

        if (isAvailable) {
          itemAnalysis.availableItems++;
        } else {
          itemAnalysis.unavailableItems++;
          itemAnalysis.unavailableItemsDetails.push({
            id: item.id,
            barcode: item.barcode || intl.formatMessage({
              id: 'ui-plugin-inventory-templates.safeDelete.field.noBarcode',
              defaultMessage: 'No barcode',
            }),
            status,
            location:
              item.effectiveLocation?.name ||
              item.permanentLocation?.name ||
              intl.formatMessage({
                id: 'ui-plugin-inventory-templates.safeDelete.field.unknownLocation',
                defaultMessage: 'Unknown location',
              }),
            callNumber:
              item.effectiveCallNumberComponents?.callNumber ||
              intl.formatMessage({
                id: 'ui-plugin-inventory-templates.safeDelete.field.noCallNumber',
                defaultMessage: 'No call number',
              }),
            holdingId: item.holdingId,
          });
        }
      });
    }

    // Determine deletion type and safety
    let deletionType = 'blocked';
    let canSafelyDelete = false;
    let safetyMessage = '';

    if (!hasHoldings && !hasItems) {
      // No holdings or items - simple deletion
      deletionType = 'instance-only';
      canSafelyDelete = true;
      safetyMessage = intl.formatMessage({
        id: 'ui-plugin-inventory-templates.safeDelete.safety.instanceOnlySafe',
        defaultMessage: 'This instance has no associated holdings or items and can be safely deleted.',
      });
    } else if (hasItems && itemAnalysis.unavailableItems === 0) {
      // All items are available - complete deletion possible
      deletionType = 'complete';
      canSafelyDelete = true;
      safetyMessage = intl.formatMessage(
        {
          id: 'ui-plugin-inventory-templates.safeDelete.safety.completeDeletionSafe',
          defaultMessage: 'All items are available. Deletion will remove the instance, all holdings ({holdingsCount}), and all items ({itemsCount}).',
        },
        { holdingsCount: holdings.length, itemsCount: itemAnalysis.totalItems }
      );
    } else if (hasItems && itemAnalysis.unavailableItems > 0) {
      // Some items are unavailable - block deletion
      deletionType = 'blocked';
      canSafelyDelete = false;
      safetyMessage = intl.formatMessage(
        {
          id: 'ui-plugin-inventory-templates.safeDelete.safety.unavailableItemsBlock',
          defaultMessage: 'This instance has {unavailableCount} unavailable items. All items must be available (status: Available) before deletion.',
        },
        { unavailableCount: itemAnalysis.unavailableItems }
      );
    } else if (hasHoldings && !hasItems) {
      // Has holdings but no items - could be empty holdings
      deletionType = 'complete';
      canSafelyDelete = true;
      safetyMessage = intl.formatMessage(
        {
          id: 'ui-plugin-inventory-templates.safeDelete.safety.holdingsOnlySafe',
          defaultMessage: 'This instance has holdings with no items. Deletion will remove the instance and all holdings ({holdingsCount}).',
        },
        { holdingsCount: holdings.length }
      );
    }

    return {
      // Basic info
      hasHoldings,
      hasItems,

      // Enhanced analysis
      ...itemAnalysis,

      // Safety determination
      deletionType,
      canSafelyDelete,
      safetyMessage,

      // Additional info for UI
      holdingsCount: holdings?.length || 0,
      canShowItemDetails: itemAnalysis.unavailableItems > 0,
    };
  }, [instance, holdings, items]);
};

export default useEnhancedSafetyCheck;
